/* eslint-disable react/prop-types */
import React, { FunctionComponent, useEffect, useState, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import * as Yup from 'yup';
import { auth, db, functions } from '../../firebase';
import LoaderComponent from '../../Shared/Loader';
import logo from '../../Assets/Images/logo.png';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { MISSING_COMPANY_CLAIM_ERROR } from '../../utils/companyClaim';
import { getFormattedPhone, getIsValidPhone } from '../../utils/validatePhone';
import { inviteRegionIdFromSearch } from './inviteRegion';
import { inviteTokenFromSearch } from '../../utils/inviteTokenFromSearch';

export const SIGNUP_VALIDATION_REJECTION_FALLBACK = 'Please check the form and try again.';
export const SIGNUP_REGISTER_REJECTION_FALLBACK = 'Registration failed. Please try again.';
export const SIGNUP_REGION_LOAD_REJECTION_FALLBACK = 'Regions could not load. Please try again.';
export const SIGNUP_INVALID_LINK_ERROR =
    "This link is invalid. Please contact an administrator if you don't know your region";

export interface ILocation {
    search: string;
    hash: string;
    key: string;
    pathname: string;
    state: string;
}

interface Props {
    location: ILocation;
}

export const SIGNUP_PASSWORD_MIN_MESSAGE = 'Password must be at least 6 characters';
export const SIGNUP_PHONE_INVALID_MESSAGE = 'Phone number is invalid';

export const signupSchema = Yup.object().shape({
    password: Yup.string().min(6, SIGNUP_PASSWORD_MIN_MESSAGE).required('This field is required'),
});

const SignupPage: FunctionComponent<Props> = ({ location }) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [techRegion, setTechRegion] = useState('');
    const [availableRegions, setAvailableRegions] = useState<Array<{ name: string; center: any; id: string }>>([]);
    const [showLoader, setShowLoader] = useState(false);
    const [isRegionIdSupplied, setIsRegionIdSupplied] = useState(false);
    const registerLock = useRef(false);
    const [registerLocked, setRegisterLocked] = useState(false);

    const releaseRegisterLock = () => {
        registerLock.current = false;
        setRegisterLocked(false);
    };

    const createUserWithEmailAndPasswordHandler = async (event) => {
        event.preventDefault();
        if (registerLock.current) return;
        registerLock.current = true;
        setRegisterLocked(true);
        try {
            await signupSchema.validate({ password });
        } catch (validationError) {
            setError(rejectionMessage(validationError, SIGNUP_VALIDATION_REJECTION_FALLBACK));
            releaseRegisterLock();
            return;
        }
        const formattedPhone = getFormattedPhone(phone);
        if (!getIsValidPhone(phone) || formattedPhone == null) {
            setError(SIGNUP_PHONE_INVALID_MESSAGE);
            releaseRegisterLock();
            return;
        }
        setPhone(formattedPhone);
        setShowLoader(true);
        try {
            await functions.httpsCallable('registerTech')({
                email,
                password,
                regionId: techRegion,
                name,
                phone: formattedPhone,
                ...(inviteToken ? { inviteToken } : {}),
            });
            // const { user } = await auth.createUserWithEmailAndPassword(mail, pass);
            await auth.signInWithEmailAndPassword(email, password);
            setShowLoader(false);
            releaseRegisterLock();
        } catch (errors) {
            setError(rejectionMessage(errors, SIGNUP_REGISTER_REJECTION_FALLBACK));
            setShowLoader(false);
            releaseRegisterLock();
        }
    };

    const onChangeHandler = (setFunc: (value: any) => void) => (evt) => setFunc(evt.currentTarget.value);

    const queryRegionId = useMemo(() => inviteRegionIdFromSearch(location.search), [location.search]);
    const inviteToken = useMemo(() => inviteTokenFromSearch(location.search), [location.search]);

    // Invite links resolve a single region via getDoc (allowed logged-out).
    // Collection list stays admin/member-only and is only used without regionID.
    // A missing doc is an invalid link. A rejected get or regions snapshot is a load
    // failure: show a trimmed alert and leave Register usable.
    useEffect(() => {
        let cancelled = false;

        if (queryRegionId) {
            setIsRegionIdSupplied(false);
            setError('');
            db.doc(`/regions/${queryRegionId}`)
                .get()
                .then((regionDoc) => {
                    if (cancelled) return;
                    if (regionDoc.exists) {
                        setAvailableRegions([{ ...(regionDoc.data() as any), id: regionDoc.id }]);
                        setTechRegion(regionDoc.id);
                        setIsRegionIdSupplied(true);
                        setError('');
                        return;
                    }
                    setAvailableRegions([]);
                    setTechRegion('');
                    setIsRegionIdSupplied(false);
                    setError(SIGNUP_INVALID_LINK_ERROR);
                })
                .catch((loadError) => {
                    if (cancelled) return;
                    console.error('Unable to load region', loadError);
                    setAvailableRegions([]);
                    setTechRegion(queryRegionId);
                    setIsRegionIdSupplied(true);
                    setError(rejectionMessage(loadError, SIGNUP_REGION_LOAD_REJECTION_FALLBACK));
                });
            return () => {
                cancelled = true;
            };
        }

        setIsRegionIdSupplied(false);
        setTechRegion('');
        setAvailableRegions([]);
        // Logged-out signup has no ID token. Listing every region would be an
        // unfiltered collection query, which the tenant rules deny. Invite links
        // still load one region with getDoc above.
        setError(MISSING_COMPANY_CLAIM_ERROR);
        return undefined;
    }, [queryRegionId]);

    return (
        <div className="bg-cover min-h-screen bg-gradient-to-b from-black to-blue-500">
            <LoaderComponent showLoader={showLoader} />
            <div className="flex flex-col">
                <div className="mt-32 text-center mx-auto">
                    <img src={logo} alt="Logo" className="text-center mx-auto w-8/12" />
                    <p className="text-lg  font-medium text-center text-white mt-4">Technician Scheduling Made Easy</p>
                </div>
                <div className="text-center  mt-16">
                    {error && <div className="py-4 bg-red-500 w-full text-white text-center mb-3">{error}</div>}
                    <form
                        className="flex-col"
                        onSubmit={(event) => {
                            createUserWithEmailAndPasswordHandler(event);
                        }}
                    >
                        <div className="my-4">
                            <input
                                required
                                type="text"
                                name="name"
                                value={name}
                                id="name"
                                onChange={onChangeHandler(setName)}
                                placeholder="Name"
                                className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                            />
                        </div>
                        <div className="my-4">
                            <input
                                required
                                type="email"
                                name="userEmail"
                                value={email}
                                placeholder="Email"
                                id="userEmail"
                                onChange={onChangeHandler(setEmail)}
                                className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                            />
                        </div>
                        <div className="my-4">
                            <input
                                required
                                type="tel"
                                name="phone"
                                value={phone}
                                id="phone"
                                placeholder="Phone Number"
                                onChange={onChangeHandler(setPhone)}
                                onBlur={() => {
                                    if (!getIsValidPhone(phone)) {
                                        return;
                                    }
                                    const formatted = getFormattedPhone(phone);
                                    if (formatted == null) {
                                        return;
                                    }
                                    setPhone(formatted);
                                }}
                                className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                            />
                        </div>
                        <div className="my-4">
                            <input
                                required
                                type="password"
                                name="userPassword"
                                value={password}
                                placeholder="Password"
                                id="userPassword"
                                minLength={6}
                                onChange={onChangeHandler(setPassword)}
                                className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                            />
                        </div>

                        {!queryRegionId && (
                            <div className="my-4">
                                <select
                                    className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                    required
                                    value={techRegion}
                                    onChange={onChangeHandler(setTechRegion)}
                                >
                                    <option className="placeholder-gray-600" value="">
                                        Select a region
                                    </option>
                                    {availableRegions.map((region) => (
                                        <option key={region.id} value={region.id}>
                                            {region.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="mt-8">
                            <button
                                type="submit"
                                disabled={(Boolean(queryRegionId) && !isRegionIdSupplied) || registerLocked}
                                className={`bg-primaryTheme hover:bg-blue-900 text-white font-bold py-2 px-4 rounded w-72${
                                    (queryRegionId && !isRegionIdSupplied) || registerLocked
                                        ? ' opacity-50 cursor-not-allowed'
                                        : ''
                                }`}
                            >
                                Register
                            </button>
                        </div>
                    </form>
                    <div className="my-4 text-white">
                        <p>
                            <NavLink to="/login">Already have an account?</NavLink>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default SignupPage;
